import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import { environmentLabel, useAppInfo } from '../info';
import { Modal, useToast } from '../ui';
import { Icon, type IconName } from '../ui/icons';
import { Logo, LogoMark } from '../ui/Logo';

interface DemoInfo {
  readonly password: string;
  readonly accounts: readonly { readonly email: string; readonly label: string; readonly fullName: string }[];
}

type LoginAnswer = { readonly token: string; readonly usedRecoveryCode?: boolean } | { readonly twoFactorRequired: true; readonly challenge: string };

export function Login({ onToken }: { onToken: (token: string) => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState<DemoInfo | null>(null);
  // step two of a user with two-factor sign-in: the password was right
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  // "Şifremi unuttum": null while signing in, then the form, then how help comes (a link by e-mail, or people)
  const [forgot, setForgot] = useState<'ask' | ForgotAnswer | null>(null);
  const info = useAppInfo();
  const ribbon = environmentLabel(info?.environment);
  const toast = useToast();

  // only the local demo server answers this: it lists its made-up accounts
  useEffect(() => {
    api<DemoInfo>('GET', '/api/demo')
      .then(setDemo)
      .catch(() => setDemo(null));
  }, []);

  const submit = async (event?: FormEvent, as?: { email: string; password: string }) => {
    event?.preventDefault();
    const credentials = as ?? { email, password };
    setBusy(true);
    setError(null);
    try {
      const answer = await api<LoginAnswer>('POST', '/api/auth/login', credentials);
      if ('twoFactorRequired' in answer) {
        setChallenge(answer.challenge);
        setCode('');
        setBusy(false);
        return;
      }
      await onToken(answer.token);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await api<{ token: string; usedRecoveryCode: boolean }>('POST', '/api/auth/login/2fa', { challenge, code: code.trim() });
      if (answer.usedRecoveryCode) toast('Bir kurtarma kodu kullandınız; Güvenlik ekranından yenilerini oluşturabilirsiniz', 'info');
      await onToken(answer.token);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
      // the five minutes are over: back to the password
      if (e instanceof Error && /süresi doldu/.test(e.message)) setChallenge(null);
    }
  };

  return (
    <div className="login with-hero">
      <section className="login-hero" aria-label="Pronto Depo">
        <Logo size={44} />
        <div className="login-hero-body">
          <h1>
            Depo ve <em>satıcı paneli</em>
          </h1>
          <p>Satıcıların ürünleri rafta, siparişleri yolda: kabulden kargoya her adım tek yerde.</p>
          <ul>
            {POINTS.map(([icon, text]) => (
              <li key={text}>
                <Icon name={icon} size={18} />
                {text}
              </li>
            ))}
          </ul>
        </div>
        <p className="login-foot">Pronto Depo, bir Yılmazer Lojistik hizmetidir</p>
      </section>
      <main className="login-main">
      <div className="card login-card">
        <div className="login-card-h">
          <LogoMark size={40} />
          <div>
            <h2>Giriş yap</h2>
            <span className="muted">Depo ekibi ve satıcılar aynı yerden girer</span>
          </div>
        </div>
        {ribbon && <div className={`banner ${ribbon.tone}`} style={{ borderRadius: 0 }}>{ribbon.text}</div>}
        {challenge ? (
          <form className="card-b stack" onSubmit={(e) => void submitCode(e)}>
            {error && <div className="banner bad">{error}</div>}
            <p className="muted" style={{ margin: 0 }}>
              {recovery
                ? 'Telefonunuz yanınızda değilse kurtarma kodlarınızdan birini girin (her kod bir kez çalışır).'
                : 'Telefonunuzdaki doğrulama uygulamasının gösterdiği 6 haneli kodu girin.'}
            </p>
            <div className="field">
              <label htmlFor="code">{recovery ? 'Kurtarma kodu' : 'Doğrulama kodu'}</label>
              <input
                id="code"
                className="input mono"
                inputMode={recovery ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoFocus
                maxLength={recovery ? 11 : 7}
                placeholder={recovery ? 'XXXXX-XXXXX' : '123456'}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={{ fontSize: 22, letterSpacing: 3 }}
                required
              />
            </div>
            <button className="btn primary big block" disabled={busy || code.trim().length < 6}>
              {busy ? 'Doğrulanıyor…' : 'Giriş yap'}
            </button>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="btn ghost sm" onClick={() => setRecovery((r) => !r)}>
                {recovery ? 'Uygulama kodunu gir' : 'Telefonum yanımda değil'}
              </button>
              <button type="button" className="btn ghost sm" onClick={() => setChallenge(null)}>
                Başka hesapla gir
              </button>
            </div>
          </form>
        ) : (
        <form className="card-b stack" onSubmit={submit}>
          {error && <div className="banner bad">{error}</div>}
          <div className="field">
            <label htmlFor="email">E-posta</label>
            <input id="email" className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="password">Şifre</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button className="btn primary big block" disabled={busy}>
            {busy ? 'Giriş yapılıyor…' : 'Giriş yap'}
          </button>
          <button type="button" className="btn ghost sm" style={{ alignSelf: 'center' }} onClick={() => (setForgot('ask'), setError(null))}>
            Şifremi unuttum
          </button>
        </form>
        )}
        {forgot && (
          <ForgotPassword email={email} sent={forgot === 'ask' ? null : forgot} onSent={setForgot} onClose={() => setForgot(null)} />
        )}
        {demo && !challenge && (
          <div className="card-b" style={{ borderTop: '1px solid var(--line)' }}>
            <div className="row" style={{ marginBottom: 8 }}>
              <span className="chip warn">Demo</span>
              <span className="muted" style={{ fontSize: 13 }}>
                Yerel demo sunucusu. Bir hesap seçin:
              </span>
            </div>
            <div className="demo-accounts">
              {demo.accounts.map((a) => (
                <button key={a.email} type="button" disabled={busy} onClick={() => void submit(undefined, { email: a.email, password: demo.password })}>
                  <span>
                    <b>{a.label}</b>
                    <br />
                    <small>{a.fullName}</small>
                  </span>
                  <small>{a.email}</small>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="login-foot small">Pronto Depo, bir Yılmazer Lojistik hizmetidir</p>
      </main>
    </div>
  );
}

/** How help comes: a link by e-mail, or the people who can give a temporary password are told in the panel. */
type ForgotAnswer = 'email' | 'recent' | 'people';

/**
 * "Şifremi unuttum": with e-mail set up a single-use link goes to the account's address; otherwise the
 * people who can give a temporary password (the warehouse's managers, and for a seller's user the seller's
 * owner) are told in the panel. An address with no account is said so, to be corrected.
 */
function ForgotPassword(props: { email: string; sent: ForgotAnswer | null; onSent: (by: ForgotAnswer) => void; onClose: () => void }) {
  const [email, setEmail] = useState(props.email);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ask = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const answer = await api<{ by?: 'email' | 'people'; recent?: boolean }>('POST', '/api/auth/forgot', { email: email.trim() });
      props.onSent(answer.by !== 'email' ? 'people' : answer.recent ? 'recent' : 'email');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Şifremi unuttum" onClose={props.onClose}>
      {props.sent ? (
        <div className="stack">
          <div className="banner ok">İsteğiniz iletildi.</div>
          {props.sent === 'email' ? (
            <p style={{ margin: 0 }}>
              <b>{email.trim()}</b> adresine 30 dakika geçerli bir şifre yenileme bağlantısı gönderdik. Birkaç dakika içinde gelmezse spam
              klasörüne bakın; yine gelmezse firmanızın yöneticisine ya da depo yöneticisine başvurun.
            </p>
          ) : props.sent === 'recent' ? (
            <p style={{ margin: 0 }}>
              <b>{email.trim()}</b> adresine az önce bir şifre yenileme bağlantısı gönderildi. Gelen kutunuzu ve spam klasörünü kontrol edin;
              birden fazla e-posta varsa en sonuncusundaki bağlantıyı kullanın. Yeni bir bağlantı için birkaç dakika sonra tekrar deneyin.
            </p>
          ) : (
            <p style={{ margin: 0 }}>
              Hesabınızı yöneten kişi (firmanızın yöneticisi ya da depo yöneticisi) size geçici bir şifre verecek. Onunla girince kendi şifrenizi
              belirlersiniz. Uzun sürerse ona doğrudan ulaşın.
            </p>
          )}
          <button className="btn" onClick={props.onClose}>
            Tamam
          </button>
        </div>
      ) : (
        <form className="stack" onSubmit={(e) => void ask(e)}>
          {error && <div className="banner bad">{error}</div>}
          <p style={{ margin: 0 }}>Hesabınızın e-postasını yazın; şifrenizi yenilemeniz için yol gösterelim.</p>
          <div className="field">
            <label htmlFor="forgot-email">E-posta</label>
            <input id="forgot-email" className="input" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <button className="btn primary" disabled={busy || !email.trim()}>
            {busy ? 'Gönderiliyor…' : 'Yardım iste'}
          </button>
        </form>
      )}
    </Modal>
  );
}

const POINTS: readonly (readonly [IconName, string])[] = [
  ['grid', 'Göz göz stok: lot, son kullanma tarihi ve sayım'],
  ['plug', 'Trendyol, Hepsiburada, n11, Amazon gibi 10 pazaryerinin ve kendi sitenizin siparişleri tek ekranda'],
  ['route', 'Haritalı toplama rotası, okutarak paketleme, hazır kargo etiketi'],
];
